import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@roger/ui-components';
import { useStore } from '../../store';
import {
  composerInputId,
  useConversation,
} from '../../hooks/chat/useConversation';
import {
  useChannelMemberSearch,
  useInbox,
} from '../../hooks/chat/useChatQueries';
import {
  useChangeMessage,
  useSetReaction,
} from '../../hooks/chat/useChatMutations';
import { useDebounce } from '../../hooks/useDebounce';
import {
  isInChat,
  isTextEntry,
  restoredWindowIds,
} from '../../hooks/chat/chatFocus';
import type {
  AttachmentOut,
  ChannelMemberOption,
  MessageOut,
} from '../../services/api';
import { AttachButton } from './AttachButton';
import { AttachmentLightbox } from './AttachmentLightbox';
import { mentionsToText } from './mentions';
import { MessageList } from './MessageList';
import { ConnectionStrip } from './ConnectionStrip';
import { PendingAttachmentChip } from './PendingAttachmentChip';
import { SendBar } from './SendBar';
import { isCareTeam, isDirectChannel } from './windowHeader';

// The last scrollTop per channel. Session memory, not storage: a minimized
// window unmounts its list, and restoring it should land where it was left.
// Absent means at the bottom. `seq` is the newest message at save time.
const scrollTops = new Map<string, { top: number; seq: number }>();

// The closer of the one attachment preview open across all chat windows: a
// second preview closes the first, so only one keyboard listener is live.
let closeOtherPreview: (() => void) | undefined;

/**
 * "<sender>: <body>" of the newest message from someone else that arrived
 * after the history loaded. History loaded on open (including a stale cache
 * refetched on mount) and your own messages announce nothing.
 */
function useLiveAnnouncement(
  messages: MessageOut[],
  ready: boolean,
  currentUserId: string
): { text: string; n: number } {
  const seen = useRef<number | null>(null);
  // `n` re-keys the node, so the same text twice is announced twice.
  const [announcement, setAnnouncement] = useState({ text: '', n: 0 });
  const newest = messages[messages.length - 1]?.seq ?? 0;
  useEffect(() => {
    if (!ready) return;
    const before = seen.current;
    seen.current = newest;
    if (before === null || newest <= before) return;
    const latest = messages
      .filter(m => m.seq > before && m.sender_id !== currentUserId)
      .pop();
    if (latest)
      setAnnouncement(current => ({
        text: `${latest.sender_name}: ${mentionsToText(latest.body)}`,
        n: current.n + 1,
      }));
  }, [ready, newest]);
  return announcement;
}

export function ConversationBody({
  channelId,
  onGone,
  touch = false,
}: {
  channelId: string;
  onGone: () => void;
  /** Phone sheet sizing for the composer. */
  touch?: boolean;
}) {
  const currentUserId = useStore(state => state.user?.id ?? '');
  const {
    header,
    messagesQuery,
    messages,
    pending,
    loadError,
    archived,
    draft,
    setDraft,
    doSend,
    notice,
    setNotice,
    upload,
    offline,
    urls,
    download,
    handleDrop,
    handlePaste,
    canWrite,
  } = useConversation(channelId, { onGone });
  // `mentionQuery` is what the person typed; the debounce moves it into
  // `searchQuery`, which the search reads.
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string | null>(null);
  const debounceSearch = useDebounce(setSearchQuery, 150);
  const memberSearch = useChannelMemberSearch(channelId, searchQuery);
  const mentionStatus = memberSearch.isError
    ? 'error'
    : mentionQuery !== searchQuery ||
        memberSearch.isPlaceholderData ||
        memberSearch.data === undefined
      ? 'loading'
      : 'ready';
  const onMentionQueryChange = useCallback(
    (query: string | null) => {
      setMentionQuery(query);
      if (query === null) {
        debounceSearch.cancel();
        setSearchQuery(null);
      } else {
        debounceSearch(query);
      }
    },
    [debounceSearch]
  );
  const onMentionPick = (member: ChannelMemberOption) => {
    const state = useStore.getState();
    const picks = state.chatDraftMentions[channelId] ?? [];
    if (picks.some(pick => pick.id === member.id)) return;
    state.setChatDraftMentions(channelId, [
      ...picks,
      { id: member.id, name: member.name },
    ]);
  };
  const { setReaction, pendingMessageIds } = useSetReaction(channelId);
  const { editMessage, deleteMessage } = useChangeMessage(channelId);
  const inboxRow = useInbox().data?.find(row => row.channel_id === channelId);
  // History settled: the same signal the live announcement uses, and the one
  // MessageList reads to tell a live arrival from history.
  const historyReady = messagesQuery.isSuccess && !messagesQuery.isFetching;
  const announcement = useLiveAnnouncement(
    messages,
    historyReady,
    currentUserId
  );
  // Opening a desktop window focuses its composer, once it exists. The phone
  // sheet does not: the keyboard must not cover the conversation on open. A
  // chart text field the person is typing in keeps focus; a chart button
  // (the care-team button that opened this window) does not.
  const focusedComposer = useRef(false);
  useEffect(() => {
    if (focusedComposer.current) return;
    if (restoredWindowIds.delete(channelId)) {
      // A reload restored this window; only a real open focuses the composer.
      focusedComposer.current = true;
      return;
    }
    if (touch || !canWrite) return;
    const active = document.activeElement;
    if (active && isTextEntry(active) && !isInChat(active)) return;
    focusedComposer.current = true;
    document.getElementById(composerInputId(channelId))?.focus();
  }, [canWrite, channelId, touch]);
  const { chips, addFiles, remove, retry, uploading, doneIds } = upload;
  const superseded = useRef(false);
  const [lightbox, setLightbox] = useState<{
    tiles: AttachmentOut[];
    index: number;
  } | null>(null);

  return (
    <div
      data-testid='conversation-body'
      className='flex min-h-0 flex-1 flex-col'
      onDragOver={event => event.preventDefault()}
      onDrop={handleDrop}
      onPaste={handlePaste}
    >
      <ConnectionStrip />
      {header?.is_member === false && !archived && (
        <p className='border-b px-3 py-2 text-center text-xs text-muted-foreground'>
          {header.is_agency_channel
            ? 'You are not a member of this channel. You can post here because you are an admin.'
            : 'You are not in this clinic. You can post here because you are an admin.'}
        </p>
      )}
      <div
        data-testid='chat-live-region'
        aria-live='polite'
        className='sr-only'
      >
        <span key={announcement.n}>{announcement.text}</span>
      </div>

      {loadError ? (
        <p className='flex-1 px-6 py-8 text-center text-sm text-muted-foreground'>
          Messages are not available.
        </p>
      ) : !messagesQuery.isLoading &&
        messages.length === 0 &&
        pending.length === 0 ? (
        // Errors table: an empty channel gets short centered copy and one action.
        <div className='flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center'>
          <p className='text-sm text-muted-foreground'>No messages yet</p>
          {header?.can_write && !archived && (
            <Button
              size='sm'
              onClick={() =>
                document.getElementById(composerInputId(channelId))?.focus()
              }
            >
              Write a message
            </Button>
          )}
        </div>
      ) : (
        <MessageList
          messages={messages}
          pending={pending}
          currentUserId={currentUserId}
          isGroup={!!header && !isDirectChannel(inboxRow, header)}
          lastReadSeq={header?.last_read_seq ?? 0}
          historyReady={historyReady}
          showNewDivider={header?.is_member !== false}
          hasOlder={!!messagesQuery.hasNextPage}
          isLoadingOlder={messagesQuery.isFetchingNextPage}
          onLoadOlder={() => void messagesQuery.fetchNextPage()}
          onRetry={(clientId, body) => void doSend(body, clientId)}
          urls={urls}
          touch={touch}
          canReact={!archived}
          onToggleReaction={setReaction}
          pendingReactionIds={pendingMessageIds}
          canChange={canWrite}
          onEditMessage={editMessage}
          onDeleteMessage={deleteMessage}
          onDownloadAttachment={id => void download(id)}
          onOpenAttachment={(tiles, index) => {
            closeOtherPreview?.();
            superseded.current = false;
            closeOtherPreview = () => {
              // Closed because another preview opened: focus stays put.
              superseded.current = true;
              setLightbox(null);
            };
            setLightbox({ tiles, index });
          }}
          initialScrollTop={scrollTops.get(channelId)?.top}
          initialNewestSeq={scrollTops.get(channelId)?.seq}
          onScrollTopChange={(top, seq) =>
            top === null
              ? scrollTops.delete(channelId)
              : scrollTops.set(channelId, { top, seq })
          }
        />
      )}

      {loadError ? null : archived ? (
        <p className='border-t px-3 py-3 text-center text-xs text-muted-foreground'>
          This conversation is archived.
        </p>
      ) : header?.can_write ? (
        <SendBar
          inputId={composerInputId(channelId)}
          title={isCareTeam(header) ? 'the care team' : header.title}
          touch={touch}
          value={draft}
          onChange={setDraft}
          onSend={() => void doSend(draft)}
          disabled={uploading}
          mentions={{
            results: memberSearch.data ?? [],
            status: mentionStatus,
            onQueryChange: onMentionQueryChange,
            onPick: onMentionPick,
          }}
          canSendWhenEmpty={doneIds.length > 0}
          aboveInput={
            chips.length > 0 || notice ? (
              <div className='flex flex-col gap-1 pb-2'>
                {chips.map(chip => (
                  <PendingAttachmentChip
                    key={chip.localId}
                    chip={chip}
                    onRemove={remove}
                    onRetry={retry}
                  />
                ))}
                {notice && <p className='text-xs text-destructive'>{notice}</p>}
              </div>
            ) : undefined
          }
          toolbarStart={
            <AttachButton
              existingCount={chips.length}
              disabled={offline}
              onFiles={addFiles}
              onNotice={setNotice}
              touch={touch}
            />
          }
        />
      ) : (
        <p className='border-t px-3 py-3 text-center text-xs text-muted-foreground'>
          Only announcers can post here.
        </p>
      )}
      {lightbox && (
        <AttachmentLightbox
          attachments={lightbox.tiles}
          index={lightbox.index}
          urls={urls}
          onIndexChange={index => setLightbox({ ...lightbox, index })}
          onClose={() => setLightbox(null)}
          returnsFocus={() => !superseded.current}
          onDownload={id => void download(id)}
        />
      )}
    </div>
  );
}
